// pages/blog/[year]/[month]/[day]/[slug].jsx

import { first } from 'lodash'
import { BlogAPI } from '../../../../../apis/BlogAPI'
import { useEffect, useState } from 'react'
import { Container } from '../../../../../components/Container'
import Link from 'next/link'
import Image from 'next/image'
import ShallowHeader from '../../../../../components/ShallowHeader'
import BlogCategoriesBlock from '../../../../../components/BlogCategoriesBlock'
import BlogDetailWrapper from '../../../../../components/BlogDetailWrapper'
import { getBlogWrapperPosts } from '../../../../../helpers/blog'
import { BlogRecentPost } from '../../../../../components/BlogRecentPost'
import BlogDateInline from '../../../../../components/BlogDateInline'

export async function getStaticProps({ params }) {
    const { slug } = params

    const { data } = await BlogAPI.safeRequest(
        () => BlogAPI.getPostBySlug(slug),
        { data: [] },
        `single post getPostBySlug ${slug}`,
    )
    const post = first(data)

    if (!post) {
        return { notFound: true }
    }

    const relatedPosts = await BlogAPI.safeRequest(
        () => BlogAPI.getRandomPosts(),
        [],
        `single post getRandomPosts ${slug}`,
    )
    const { data: author } = await BlogAPI.safeRequest(
        () => BlogAPI.getAuthorById(post.author),
        { data: { name: '', slug: '' } },
        `single post getAuthorById ${post.author}`,
    )
    const { initialCategories, latestBlogPosts, tags, popularPosts } =
        await getBlogWrapperPosts()

    const { data: categories } = await BlogAPI.safeRequest(
        () => BlogAPI.getCategories(),
        { data: [] },
        'single post getCategories',
    )
    const categoriesMap = categories.reduce((acc, category) => {
        acc[category.id] = category.name
        return acc
    }, {})

    return {
        props: {
            post,
            author,
            categories: categoriesMap,
            relatedPosts,
            latestBlogPosts,
            initialCategories,
            tags,
            popularPosts,
        },
        revalidate: 10,
    }
}

export async function getStaticPaths() {
    const posts = await BlogAPI.safeRequest(
        () => BlogAPI.getAllBlogPosts(),
        [],
        'single post getStaticPaths',
    )

    const paths = posts.map((post) => {
        const date = new Date(post.date)
        const year = date.getFullYear()
        const month = `0${date.getMonth() + 1}`.slice(-2)
        const day = `0${date.getDate()}`.slice(-2)

        return {
            params: {
                year: `${year}`,
                month: `${month}`,
                day: `${day}`,
                slug: post.slug,
            },
        }
    })

    return {
        paths,
        fallback: 'blocking',
    }
}

const SinglePost = ({
    post,
    categories,
    relatedPosts,
    author,
    latestBlogPosts,
    initialCategories,
    tags,
    popularPosts,
}) => {
    const [title, setTitle] = useState('')

    useEffect(() => {
        // Fix YouTube iframes to be responsive
        const iframes = document.querySelectorAll('iframe')
        iframes.forEach((iframe) => {
            const wrapper = document.createElement('div')
            wrapper.classList.add('aspect-w-16', 'aspect-h-9', 'mb-6')
            iframe.parentNode.insertBefore(wrapper, iframe)
            wrapper.appendChild(iframe)
        })
    }, [])

    useEffect(() => {
        setTitle(post.title.rendered)
    }, [post])

    if (!post) return <p>Post not found</p>

    return (
        <Container>
            <ShallowHeader metaTitle={title}></ShallowHeader>
            <BlogDetailWrapper
                {...{
                    title,
                    latestBlogPosts,
                    initialCategories,
                    tags,
                    popularPosts,
                }}>
                <div className="single-post">
                    <article className="prose lg:prose-xl mx-auto pb-8">
                        <header>
                            <div className="text-base text-gray-aaa flex font-medium py-4 border-b border-gray-eee uppercase tracking-wide mb-2">
                                <BlogCategoriesBlock
                                    post={post}
                                    categories={categories}
                                />
                            </div>
                            <h1 className="text-40px leading-16 font-normal text-blog-black mb-4">
                                {post.title.rendered}
                            </h1>
                            <p className="text-sm text-gray-aaa mb-6">
                                By{' '}
                                <Link href={`/blog/author/${author.slug}`}>
                                    <a>{author.name}</a>
                                </Link>{' '}
                                PUBLISHED ON{' '}
                                <BlogDateInline
                                    post={post}
                                    isSidePosts={false}
                                />
                                {/* {new Date(post.date).toLocaleDateString()} */}
                            </p>
                        </header>

                        {post.jetpack_featured_media_url && (
                            <div className="relative w-full h-96 mb-6">
                                <Image
                                    src={post.jetpack_featured_media_url}
                                    alt={
                                        post._embedded['wp:featuredmedia'][0]
                                            ?.alt_text
                                    }
                                    title={
                                        post._embedded['wp:featuredmedia'][0]
                                            ?.title?.rendered
                                    }
                                    layout="fill"
                                    objectFit="cover"
                                    className="rounded-lg"
                                />
                            </div>
                        )}
                        <div
                            className="content text-gray-666 text-lg font-light leading-7"
                            dangerouslySetInnerHTML={{
                                __html: post.content.rendered,
                            }}
                        />
                    </article>
                    <div>
                        <h2 className="text-lg text-blog-black font-semibold uppercase">
                            <i className="far fa-hand-point-right"></i> You May
                            Also Like...
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                            {relatedPosts?.map((post) => (
                                <BlogRecentPost
                                    key={post.id}
                                    post={post}
                                    categories={categories}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </BlogDetailWrapper>
        </Container>
    )
}

export default SinglePost
